import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

// Compact hover/click dropdown for top-nav groups (The Firm, Practice,
// Insights). Matches the tone of LegalLinksMenu but keeps a narrow panel
// instead of a full-width mega-menu because each group holds only 2-3
// links. Opens on hover, click toggles, Escape closes.
export default function NavDropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const rootRef = useRef(null);
  const location = useLocation();

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  // Close on route change so hitting a link inside the panel dismisses it.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  const anyActive = items.some((it) =>
    location.pathname === it.to || location.pathname.startsWith(it.to + "/")
  );

  return (
    <div
      ref={rootRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid={`nav-dropdown-${testId}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] leading-[16.5px] tracking-[0.22em] uppercase font-semibold transition-colors ${
          open || anyActive ? "text-brass-deep" : "text-ink hover:text-brass"
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={`nav-dropdown-${testId}-trigger`}
      >
        {label}
        <ChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[220px] border border-hairline bg-ivory shadow-lg z-30"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid={`nav-dropdown-${testId}-panel`}
        >
          <ul className="py-2">
            {items.map((it) => (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-5 py-2.5 text-[12px] tracking-[0.18em] uppercase font-semibold transition-colors border-l-2 ${
                      isActive
                        ? "text-brass-deep border-brass bg-ivory-alt"
                        : "text-ink border-transparent hover:text-brass hover:bg-ivory-alt hover:border-brass/40"
                    }`
                  }
                  data-testid={`nav-dropdown-${testId}-item-${it.to.replace(/^\//, "").replace(/\//g, "-")}`}
                >
                  {it.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Mobile-friendly accordion — expects a section title + link list.
export function NavDropdownMobile({ label, items, onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-hairline-soft" data-testid={`mobile-nav-group-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-2.5 text-ink text-[13px] tracking-[0.22em] uppercase font-semibold"
        aria-expanded={open}
      >
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>
      {open && (
        <ul className="pb-2 pl-3">
          {items.map((it) => (
            <li key={it.to}>
              <Link
                to={it.to}
                onClick={onNavigate}
                className="block py-2 text-[12px] tracking-[0.18em] uppercase font-semibold text-ink hover:text-brass"
                data-testid={`mobile-nav-${it.to.replace(/^\//, "").replace(/\//g, "-")}`}
              >
                {it.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
